import { useEffect, useRef, useState, useCallback } from "react";
import { api, mediaSrc } from "@/lib/api";
import { X, Megaphone } from "lucide-react";

// --- Behavior constants ----------------------------------------------------
// Initial trigger: user must scroll past this many pixels to see the FIRST popup.
const INITIAL_TRIGGER_PX = 1500;
// Cooldown (in ms of ACTIVE scrolling) before another sponsored popup appears.
const ACTIVE_SCROLL_COOLDOWN_MS = 4 * 60 * 1000; // 4 minutes
// Tick rate for accumulating active-scroll time.
const TICK_MS = 1000;
// Max gap (ms) between scrolls before we stop counting (idle threshold).
const SCROLL_IDLE_MS = 1500;
// Session keys: tracks state across re-mounts within the same tab.
const SK_LAST_SHOWN = "fci_scroll_ad_last_shown_at";
const SK_SEEN_IDS = "fci_scroll_ad_seen_ids";

/**
 * Sponsored scroll popup — appears once initially, then only re-appears after
 * 4 minutes of ACTIVE scrolling (idle time does not count). Avoids stacking
 * on top of other dialogs and respects existing fair-rotation server logic.
 *
 * Tracking:
 *  - impression: server-side via GET /api/ads/scroll (auto-increment)
 *  - click:      POST /api/ads/{id}/click
 *  - close:      POST /api/ads/{id}/close
 */
export default function ScrollAd() {
  const [ad, setAd] = useState(null);
  const [visible, setVisible] = useState(false);

  const lastScrollAtRef = useRef(0);
  const activeMsRef = useRef(0);
  const armedRef = useRef(false); // true when we're allowed to look for the next ad

  const seenIds = useCallback(() => {
    try {
      return JSON.parse(sessionStorage.getItem(SK_SEEN_IDS) || "[]");
    } catch { return []; }
  }, []);

  const rememberSeen = useCallback((id) => {
    try {
      const list = seenIds();
      if (id && !list.includes(id)) {
        list.push(id);
        sessionStorage.setItem(SK_SEEN_IDS, JSON.stringify(list.slice(-20)));
      }
    } catch {}
  }, [seenIds]);

  // Decide whether we should currently arm the popup engine.
  // After a popup is shown, we wait for 4 min of active scroll before arming again.
  useEffect(() => {
    const lastShown = parseInt(sessionStorage.getItem(SK_LAST_SHOWN) || "0", 10);
    if (!lastShown) {
      armedRef.current = true; // first session — armed immediately
    } else {
      armedRef.current = false; // wait for cooldown via accumulator
    }
  }, []);

  // Helper: is another modal/dialog currently open?
  const otherDialogOpen = () => {
    try {
      return !!document.querySelector('[role="dialog"][data-state="open"]');
    } catch { return false; }
  };

  // Fetch a fresh ad (server already applies fair rotation).
  // Skip ads we've already seen if alternatives are available.
  const fetchAd = useCallback(async () => {
    try {
      const r = await api.get("/ads/scroll", { params: { _ts: Date.now() } });
      const candidates = Array.isArray(r.data) ? r.data : [];
      if (!candidates.length) return null;
      const seen = seenIds();
      const fresh = candidates.find(a => !seen.includes(a.id));
      return fresh || candidates[0];
    } catch { return null; }
  }, [seenIds]);

  // Initial popup: load eligible ad, watch scroll position.
  useEffect(() => {
    let cancelled = false;
    if (!armedRef.current) return undefined;
    fetchAd().then(a => { if (!cancelled) setAd(a); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Scroll listener: tracks last-scroll timestamp.
  useEffect(() => {
    const onScroll = () => { lastScrollAtRef.current = Date.now(); };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Active-scroll accumulator + popup arming logic.
  useEffect(() => {
    const tick = setInterval(() => {
      const now = Date.now();
      const isScrolling = now - lastScrollAtRef.current < SCROLL_IDLE_MS;
      if (isScrolling) activeMsRef.current += TICK_MS;

      if (visible) return; // already showing — nothing to do
      if (otherDialogOpen()) return; // never stack
      if (!ad) return; // nothing to show

      const lastShown = parseInt(sessionStorage.getItem(SK_LAST_SHOWN) || "0", 10);
      if (!lastShown) {
        // First popup of the session: classic scroll-position trigger
        if (window.scrollY > INITIAL_TRIGGER_PX) {
          setVisible(true);
          sessionStorage.setItem(SK_LAST_SHOWN, String(now));
        }
      } else {
        // Subsequent popups: only after 4 minutes of ACTIVE scroll time
        if (activeMsRef.current >= ACTIVE_SCROLL_COOLDOWN_MS) {
          setVisible(true);
          sessionStorage.setItem(SK_LAST_SHOWN, String(now));
        }
      }
    }, TICK_MS);
    return () => clearInterval(tick);
  }, [ad, visible]);

  if (!ad || !visible) return null;

  const dismiss = (e) => {
    e?.stopPropagation();
    setVisible(false);
    activeMsRef.current = 0;
    rememberSeen(ad.id);
    api.post(`/ads/${ad.id}/close`).catch(() => {});
    // Pre-fetch the next eligible ad so cooldown leads straight to a fresh one
    fetchAd().then(a => setAd(a));
  };

  const onClick = () => {
    api.post(`/ads/${ad.id}/click`).catch(() => {});
    rememberSeen(ad.id);
    if (ad.link?.startsWith("http")) window.open(ad.link, "_blank", "noopener");
    else if (ad.link) window.location.href = ad.link;
    setVisible(false);
    activeMsRef.current = 0;
    fetchAd().then(a => setAd(a));
  };

  return (
    <div
      data-testid="scroll-ad"
      className="fixed bottom-24 left-3 right-3 sm:left-auto sm:right-6 sm:w-80 z-30 rounded-2xl overflow-hidden bg-white border border-[#E8E6E1] shadow-xl animate-in slide-in-from-bottom"
    >
      <button
        type="button"
        onClick={dismiss}
        data-testid="scroll-ad-close"
        aria-label="Fermer la publicité"
        className="absolute top-2 right-2 z-10 w-7 h-7 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black/80"
      >
        <X className="h-3.5 w-3.5" />
      </button>
      <button type="button" onClick={onClick} className="w-full text-left">
        <div className="relative h-28 bg-[#F3F1EC] overflow-hidden">
          <img src={mediaSrc(ad.image)} alt={ad.title} className="w-full h-full object-cover" />
          <div className="absolute top-2 left-2 inline-flex items-center gap-1 bg-[#1A1918]/85 text-white text-[10px] font-bold px-2 py-1 rounded-md backdrop-blur-sm">
            <Megaphone className="h-3 w-3" /> SPONSORISÉ
          </div>
        </div>
        <div className="p-3">
          <div className="font-bold text-sm text-[#1A1918] line-clamp-1">{ad.title}</div>
          {ad.subtitle && <div className="text-xs text-[#73706B] line-clamp-2 mt-0.5">{ad.subtitle}</div>}
          <div className="text-[11px] text-[#E65100] font-semibold mt-2">Voir l'offre →</div>
        </div>
      </button>
    </div>
  );
}
